<template>
  <div
    class="comment-thread"
    :class="{ 'single-line': singleLine }"
  >
    <template v-if="singleLine">
      <div
        v-if="latestComment"
        class="single-line-view"
        :class="{ 'has-error': latestComment.hasError }"
      >
        <span class="truncated-comment">{{ truncatedLatestComment }}</span>
        <span
          v-if="activeCommentCount > 1"
          class="comment-count-badge"
        >
          {{ activeCommentCount }}
        </span>
      </div>
    </template>
    <template v-else>
      <div
        v-for="(comment, index) in sortedComments"
        :key="comment.commentId"
        class="comment"
        :class="{ 'has-error': comment.hasError }"
      >
        <div class="comment-header">
          <div class="comment-info">
            <span
              class="username"
              :class="{ 'system-user': isSystemUser(comment.userName), 'error-underline': comment.hasError && isSystemUser(comment.userName) }"
            >{{ comment.userName }}</span>
            <span
              class="timestamp"
              :class="{ 'error-underline': comment.hasError && isSystemUser(comment.userName) }"
            >
              {{ formatDate(comment.timeStamp) }}
            </span>
            <span
              v-if="comment.isEdited"
              class="edited-info"
            >
              (Redigert: {{ comment.editedBy }} - {{ formatDate(comment.lastEdit) }})
            </span>
          </div>
          <div
            v-if="!readOnly"
            class="comment-actions"
          >
            <BaseIconButton
              icon="pencil"
              type="primary"
              @click="startEditing(index)"
            >
              Rediger
            </BaseIconButton>
            <BaseIconButton
              icon="trash-can"
              type="delete"
              @click="showDeleteConfirm(index)"
            >
              Slett
            </BaseIconButton>
          </div>
        </div>
        <div
          v-if="editingIndex === index"
          class="comment-edit"
        >
          <AutoExpandingTextarea
            v-model="editedComment"
            placeholder="Rediger kommentar..."
            :submit-icon="commentStore.isUpdating ? 'spinner' : 'check'"
            :submit-text="commentStore.isUpdating ? 'Oppdaterer...' : 'Oppdater'"
            :disabled="commentStore.isUpdating"
            action-buttons
            @submit="saveEdit(index)"
            @cancel="cancelEdit"
          />
        </div>
        <div
          v-else
          class="comment-content"
          :class="{ 'deleted': comment.isDeleted }"
        >
          {{ comment.comment }}
        </div>
        <div
          v-if="comment.isDeleted"
          class="deleted-info"
        >
          Slettet {{ formatDate(comment.deletedTime) }}
        </div>
      </div>

      <div
        v-if="!readOnly"
        class="new-comment"
      >
        <AutoExpandingTextarea
          v-model="newComment"
          placeholder="Skriv en ny kommentar..."
          :submit-icon="commentStore.isCreating ? 'spinner' : 'paper-plane'"
          :submit-text="commentStore.isCreating ? 'Lagrer...' : 'Legg til'"
          :disabled="commentStore.isCreating"
          action-buttons
          @submit="addComment"
          @cancel="cancelNewComment"
        />
      </div>
    </template>

    <BaseDialog
      :model-value="deleteDialogVisible"
      title="Bekreft sletting"
      @close="cancelDelete"
    >
      <p>Er du sikker på at du vil slette denne kommentaren?</p>

      <template #footer>
        <BaseIconButton
          icon="times"
          @click="cancelDelete"
        >
          Nei, avbryt
        </BaseIconButton>
        <BaseIconButton
          icon="check"
          type="delete"
          :disabled="commentStore.isDeleting"
          @click="confirmDelete"
        >
          {{ commentStore.isDeleting ? 'Sletter...' : 'Ja, slett' }}
        </BaseIconButton>
      </template>
    </BaseDialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useCommentStore } from '@/store/commentStore';
import AutoExpandingTextarea from '@/components/BaseComponents/AutoExpandingTextarea.vue';
import BaseIconButton from '@/components/BaseComponents/BaseIconButton.vue';
import BaseDialog from '@/components/BaseComponents/BaseDialog.vue';

const commentStore = useCommentStore();
const deleteDialogVisible = ref(false);
const deleteIndex = ref(-1);

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ comments: [] })
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true }
});

const emit = defineEmits(['update:modelValue']);

const commentThread = computed(() => {
  return props.modelValue || { comments: [] };
});

const sortedComments = computed(() => {
  return [...commentThread.value.comments]
    .filter(comment => !comment.isDeleted)
    .sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
});

const latestComment = computed(() => {
  return sortedComments.value[sortedComments.value.length - 1];
});

const truncatedLatestComment = computed(() => {
  if (!latestComment.value) return '';
  return latestComment.value.comment.length > 50
    ? latestComment.value.comment.substring(0, 50) + '...'
    : latestComment.value.comment;
});

const editingIndex = ref(-1);
const editedComment = ref('');
const newComment = ref('');

function formatDate (date) {
  if (!date) return '';
  return new Date(date).toLocaleString('nb-NO');
}

function startEditing (index) {
  editingIndex.value = index;
  editedComment.value = commentThread.value.comments[index].comment;
}

async function saveEdit (index) {
  if (editedComment.value.trim() && !commentStore.isUpdating) {
    const updatedComments = [...commentThread.value.comments];
    const commentData = {
      ...updatedComments[index],
      comment: editedComment.value.trim(),
      isEdited: true,
      lastEdit: new Date()
    };

    if (props.autoSave) {
      const payload = {
        DatabaseObjectId: props.header.databaseObjectId,
        RowId: props.rowId,
        ColumnName: props.header.field,
        CommentId: commentData.commentId,
        Comment: commentData.comment
      };

      const response = await commentStore.updateComment(payload);
      if (!response) {
        commentData.hasError = true;
        return;
      }
    }

    updatedComments[index] = commentData;
    emit('update:modelValue', { ...commentThread.value, comments: updatedComments });
    editingIndex.value = -1;
    editedComment.value = '';
  }
}

function cancelEdit () {
  editingIndex.value = -1;
  editedComment.value = '';
}

function isSystemUser (userName) {
  return userName.startsWith('System');
}

async function addComment () {
  if (newComment.value.trim() && !commentStore.isCreating) {
    if (props.autoSave) {
      const payload = {
        DatabaseObjectId: props.header.databaseObjectId,
        RowId: props.rowId,
        ColumnName: props.header.field,
        Comment: newComment.value.trim()
      };

      const response = await commentStore.createComment(payload);
      if (response && response.data) {
        emit('update:modelValue', response.data);
        newComment.value = '';
      }
    } else {
      const updatedComments = [...props.modelValue.comments, { comment: newComment.value.trim() }];
      emit('update:modelValue', { ...props.modelValue, comments: updatedComments });
      newComment.value = '';
    }
  }
}

function cancelNewComment () {
  newComment.value = '';
}

function showDeleteConfirm (index) {
  deleteIndex.value = index;
  deleteDialogVisible.value = true;
}

async function confirmDelete () {
  if (deleteIndex.value !== -1 && !commentStore.isDeleting) {
    const commentToDelete = commentThread.value.comments[deleteIndex.value];
    const payload = {
      DatabaseObjectId: props.header.databaseObjectId,
      RowId: props.rowId,
      ColumnName: props.header.field,
      CommentId: commentToDelete.commentId
    };

    const response = await commentStore.deleteComment(payload);
    if (response) {
      const updatedComments = commentThread.value.comments.map((comment, index) =>
        index === deleteIndex.value ? { ...comment, isDeleted: true, deletedTime: new Date() } : comment
      );
      emit('update:modelValue', { ...commentThread.value, comments: updatedComments });
      cancelDelete(); // ✅ Lukk KUN ved success
    }
    // Ved feil: IKKE lukk, la bruker prøve igjen
  }
}

function cancelDelete () {
  deleteDialogVisible.value = false;
  deleteIndex.value = -1;
}

const activeCommentCount = computed(() => {
  return commentThread.value.comments.filter(comment => !comment.isDeleted).length;
});
</script>

<style scoped>
.comment-thread {
  font-family: Arial, sans-serif;
}

.comment {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
  position: relative;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comment-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-actions {
  display: flex;
  gap: 12px;
}

.username {
  font-weight: bold;
}

.timestamp, .edited-info {
  color: #666;
  font-size: 0.9em;
}

.error-underline {
  text-decoration: underline;
  text-decoration-color: red;
  text-decoration-thickness: 1px;
}

.comment.has-error,
.single-line-view.has-error {
  border-right: 4px solid red;
  padding-right: 5px;
}

.comment-edit, .new-comment {
  margin-top: 10px;
}

.comment-content {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}

.comment-content.deleted,
.deleted-info {
  display: none;
}

.new-comment {
  margin-top: 20px;
  position: relative;
}

.single-line-view {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  height: 100%;
  box-sizing: border-box;
}

.single-line-view.has-error {
  border-right: 4px solid #dc3545;
}

.truncated-comment {
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comment-count-badge {
  background-color: #007bff;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  margin-left: 8px;
}

/* Stil for detaljvisningen */
.comment {
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* Fjern margin for enkeltlinjevisning */
.single-line .comment-thread > * {
  margin-bottom: 0;
}


.comment-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
